<template>
  <div v-if="isLoaded">
    <v-row>
      <v-col cols="12" md="10"><h1 class="font-weight-thin">Auto Assignment Rules</h1></v-col>
      <v-col class="text-right"
        ><v-btn
          @click="showForm = true"
          v-if="!showForm"
          class="mr-8 mt-2"
          color="primary"
          data-cy="showAutoAssignmentFormButton"
          elevation="0"
          small
          >Create</v-btn
        ></v-col
      >
    </v-row>

    <v-divider class="my-5"></v-divider>

    <v-expansion-panels focusable class="mb-5 auto-assign-instructions" style="max-width: 680px">
      <v-expansion-panel data-cy="auto-assignment-instructions-panel">
        <v-expansion-panel-header>
          <span class="d-flex align-center">
            <v-icon small color="primary" class="mr-2">fas fa-info-circle</v-icon>
            How to set up auto-assignment rules
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <instructions></instructions>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <transition name="fade-form">
      <auto-assignment-form
        @closeForm="closeForm"
        v-show="showForm"
        @updateEditIndex="updateEditIndex"
        :editedIndex="editedIndex"
        :editedItem="editedItem"
      ></auto-assignment-form>
    </transition>

    <transition name="fade-grid">
      <div v-show="!showForm">
        <v-card elevation="0">
          <v-card-text class="pt-0">
            <v-data-table
              :headers="headers"
              :items="formattedAutoConfiguration"
              disable-pagination
              elevation="0"
              hide-default-footer
            >
              <template v-slot:item.index="{ item }">
                <div class="pl-2 text-h5">
                  {{ item.index }}
                </div>
              </template>
              <template v-slot:item.filterList="{ item }">
                <ul>
                  <li v-for="(filter, index) in item.filterList" :key="index">
                    {{ getFilterLabel(filter) }}
                  </li>
                </ul>
              </template>
              <template v-slot:item.assignee="{ item }">
                {{ formatUsername(item.assignee, userMap) }}
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn
                  @click="deleteRule(item)"
                  class="mr-5"
                  color="red"
                  data-cy="deleteRuleDialog"
                  justify-center
                  outlined
                  small
                >
                  Delete
                </v-btn>
                <v-btn
                  @click="editRule(item)"
                  class="mr-5"
                  color="primary"
                  data-cy="editRuleButton"
                  justify-center
                  outlined
                  small
                >
                  Edit
                </v-btn>

                <v-icon
                  class="ml-2"
                  @click="moveRule(item, 1)"
                  v-if="isMovementUpArrowDisplayed(item)"
                  data-cy="moveRuleUpButton"
                >
                  fas fa-angle-up fa-fw
                </v-icon>
                <v-icon class="ml-2" v-else>fas fa-fw</v-icon>
                <v-icon
                  class="ml-2"
                  @click="moveRule(item, -1)"
                  data-cy="moveRuleDownButton"
                  v-if="isMovementDownArrowDisplayed(item)"
                >
                  fas fa-angle-down fa-fw
                </v-icon>
                <v-icon class="ml-2" v-else>fas fa-fw</v-icon>
              </template>
              <template v-slot:body.append="{}">
                <tr>
                  <td class="subtitle-2">Catch-All</td>
                  <td></td>
                  <td>
                    <span v-if="showDefaultAssigneeForm">
                      <user-picker
                        attributeName="defaultAssignee"
                        class="mt-5"
                        label="Catch-All Assignee"
                        outlined
                        v-model="selectedDefaultAssignee"
                      >
                      </user-picker>
                    </span>
                    <span data-cy="defaultAssigneeValue" v-else>{{
                      formatDefaultAssignee(defaultAssignee) || "unassigned"
                    }}</span>
                  </td>
                  <td class="text-right">
                    <span v-if="showDefaultAssigneeForm">
                      <v-btn
                        @click="showDefaultAssigneeForm = false"
                        class="mr-2"
                        color="grey darken-2"
                        data-cy="defaultAssigneeFormCancelButton"
                        outlined
                        small
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        @click="saveDefaultAssignee"
                        color="primary"
                        data-cy="defaultAssigneeFormSaveButton"
                        elevation="0"
                        small
                        style="margin-right: 105px"
                      >
                        Save
                      </v-btn>
                    </span>
                    <span v-else>
                      <v-btn
                        @click="loadDefaultAssigneeForm"
                        color="primary"
                        data-cy="defaultAssigneeFormEditButton"
                        justify-center
                        outlined
                        small
                        style="margin-right: 100px"
                      >
                        Edit
                      </v-btn>
                    </span>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
        <v-dialog v-model="dialogDelete" max-width="500px">
          <v-card>
            <v-card-title>
              <span class="headline font-weight-thin">Delete Confirmation</span>
            </v-card-title>
            <v-card-text>Are you sure you want to delete this rule?</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="black" outlined @click="closeDelete">Cancel</v-btn>
              <v-btn
                @click="deleteRuleConfirm"
                class="elevation-0 font-weight-bold"
                color="red"
                data-cy="deleteRuleButton"
                outlined
                >Delete</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-divider class="mt-4"></v-divider>
        <v-alert
          class="mb-0 mt-3"
          type="info"
          transition="slide-y-transition"
          dense
          v-if="hasUnavailableFilterValues"
          outlined
          role="alert"
        >
          Some filter values no longer exist in Continuum and are shown as <strong>{{ unavailableLabel }}</strong>. Those
          values will be skipped during matching, but any other values in the same filter still apply.
        </v-alert>
        <div class="ml-4 mt-6">
          <div style="max-width: 700px">
            <h2 class="font-weight-thin mb-4">Run Auto Assignment on Demand</h2>
            <p>
              Use this tool to re-apply your auto-assignment rules against a specific pack at any time — without waiting
              for the pack to move into UAT. This is useful when release notes are added late, rules have changed since
              the pack entered UAT, or a long-running pack needs to be re-evaluated.
            </p>
            <p>When you run on-demand assignment:</p>
            <ul class="mb-6 mt-3">
              <li class="mb-3">
                <b>Unassigned release notes</b> that match a rule's filters will be assigned to the rule's assignee.
              </li>
              <li class="mb-3">
                <b>Already-assigned release notes with a status of Not Started</b> will be reassigned if a rule now
                points to a different user.
              </li>
              <li class="mb-3">
                <b>Release notes with any other status</b> (In Process, Question, Failed, etc.) will not be
                changed, even if the current assignee no longer matches a rule.
              </li>
              <li>
                <b>Rules apply in precedence order</b>, the same as during UAT auto-assignment. The Catch-All assignee
                covers any release notes not matched by a specific rule.
              </li>
            </ul>
          </div>
          <v-alert v-if="packListError" type="error" outlined class="mb-4" style="max-width: 700px">
            Failed to load the pack list. Refresh the page to try again.
          </v-alert>
          <v-checkbox
            v-model="showActivePacksOnly"
            label="Show active packs only"
            dense
            hide-details
            class="mt-0 mb-3"
          ></v-checkbox>
          <v-autocomplete
            v-if="!packListError"
            v-model="selectedPackId"
            :items="filteredPackList"
            item-text="title"
            item-value="id"
            label="Select Pack"
            outlined
            dense
            clearable
            style="max-width: 700px"
            data-cy="on-demand-pack-selector"
            @change="onPackSelected"
          ></v-autocomplete>
          <router-link
            v-if="selectedPackId"
            :to="releaseNoteListRoute"
            class="d-block mb-4 text-body-2"
            data-cy="view-release-notes-link"
          >Go to release note list for this pack →</router-link>
          <v-btn
            color="primary"
            elevation="0"
            :loading="isRunningOnDemand"
            :disabled="!selectedPackId || isRunningOnDemand"
            @click="runOnDemand"
            data-cy="run-auto-assignment-on-demand-button"
          >
            Run Auto Assignment
          </v-btn>

          <v-row v-if="showNoChangesInfo" no-gutters class="mt-4">
            <v-col cols="12" lg="6">
              <v-alert
                type="info"
                outlined
                dismissible
                data-cy="no-changes-info-alert"
                @input="showNoChangesInfo = false"
              >
                No reassignments were needed. Runs with no changes are not recorded in the run history.
              </v-alert>
            </v-col>
          </v-row>

          <div v-if="selectedPackId" class="mt-6">
            <h3 class="font-weight-thin mb-3">Run History</h3>
            <v-data-table
              :headers="runHistoryHeaders"
              :items="runHistory"
              :loading="isLoadingRunHistory"
              item-key="logId"
              show-expand
              dense
              disable-pagination
              hide-default-footer
              no-data-text="No runs recorded for this pack"
              data-cy="auto-assignment-run-history-table"
            >
              <template v-slot:item.content.triggeredAt="{ item }">
                {{ formatDateTime(item.content.triggeredAt) }}
              </template>
              <template v-slot:item.content.mode="{ item }">
                {{ item.content.mode === "uat" ? "UAT" : "On Demand" }}
              </template>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-4">
                  <template v-if="item.content.changes">
                    <table v-if="item.content.changes.length" class="run-history-changes-table">
                      <thead>
                        <tr>
                          <th scope="col" style="width: 55%">Release Note</th>
                          <th scope="col" style="width: 22.5%">From</th>
                          <th scope="col" style="width: 22.5%">To</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="change in item.content.changes" :key="change.releaseNoteId">
                          <td>
                            <router-link :to="{ name: 'releaseNoteView', params: { packId: selectedPackId, releaseNoteId: change.releaseNoteId } }">
                              {{ change.title ? `${change.title} (ID:${change.releaseNoteId})` : change.releaseNoteId }}
                            </router-link>
                          </td>
                          <td>{{ formatUsername(change.fromAssignee, userMap) }}</td>
                          <td>{{ formatUsername(change.toAssignee, userMap) }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <span v-else class="grey--text text--darken-1">No changes were recorded for this run.</span>
                  </template>
                  <span v-else class="grey--text text--darken-1">
                    Change details were not collected into run history for this run. Individual note changes are still
                    available in each note's change history.
                  </span>
                </td>
              </template>
            </v-data-table>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { cloneDeep } from "lodash";
import { mapGetters, mapActions, mapState } from "vuex";
import Form from "./Form";
import Instructions from "./Instructions.vue";

const UNAVAILABLE_LABEL = "[unavailable]";

import FilterList from "@/components/FilterList";
import UserPicker from "@/components/userPicker";

import { formatUsername, getFilterLabel } from "@/lib/formatters";

export default {
  components: {
    AutoAssignmentForm: Form,
    FilterList,
    Instructions,
    UserPicker,
  },
  computed: {
    allSearchableFields() {
      return this.fullSearchableFieldList;
    },
    releaseNoteListRoute() {
      return { name: "releaseNoteList", params: { packId: this.selectedPackId } };
    },
    filteredPackList() {
      return this.showActivePacksOnly ? this.packList : this.fullPackList;
    },
    ...mapGetters({
      defaultAssignee: "autoAssignment/getDefaultAssignee",
      ruleList: "autoAssignment/getRuleList",
      runHistory: "autoAssignment/getRunHistory",
      userMap: "user/getUserMap",
      packList: "pack/getActivePackList",
    }),
    ...mapState({
      fullSearchableFieldList: state => state.searchableField.list,
      dropdownList: state => state.admin.dropdown.list,
      fullPackList: state => state.pack.packList,
    }),
    formattedAutoConfiguration() {
      return this.ruleList.map((rule, index) => {
        return Object.assign({}, rule, {
          filterList: this.formatFilterList(rule.filterList),
          index: index + 1,
        });
      });
    },
    hasUnavailableFilterValues() {
      return this.formattedAutoConfiguration.some(rule =>
        rule.filterList.some(filter => filter.valueLabel?.includes(UNAVAILABLE_LABEL))
      );
    },
  },
  watch: {
    showActivePacksOnly() {
      const stillInList = this.filteredPackList.some(p => p.id === this.selectedPackId);
      if (!stillInList) {
        this.selectedPackId = null;
      }
    },
    selectedPackId(newVal) {
      if (newVal) {
        this.showNoChangesInfo = false;
      }
    },
  },
  async created() {
    try {
      await Promise.all([this.refreshRuleTable(), this.fetchDropdownList()]);
      this.isLoaded = true;
    } catch (e) {
      console.error(e);
      this.$notifyError("An error occurred loading auto-assignment configuration");
      this.isLoaded = true;
    }
    try {
      await this.fetchPackList();
    } catch (e) {
      console.error(e);
      this.packListError = true;
    }
  },
  data() {
    return {
      dialogDelete: false,
      enableSaveRuleButton: false,
      editedIndex: -1,
      getFilterLabel,
      formatUsername,
      isLoaded: false,
      unavailableLabel: UNAVAILABLE_LABEL,
      headers: [
        {
          align: "start",
          sortable: false,
          text: "Precedence",
          value: "index",
          width: "5%",
        },
        {
          align: "start",
          sortable: false,
          text: "Filter List",
          value: "filterList",
          width: "30%",
        },
        {
          text: "Assignee",
          value: "assignee",
        },
        {
          align: "right",
          sortable: false,
          text: "Actions",
          value: "actions",
          width: "300px",
        },
      ],
      editedItem: {
        ruleList: {
          assignee: null,
          editConfirmed: false,
          filterList: [
            {
              attributeName: null,
              attributeNameLabel: null,
              dataType: null,
              operator: null,
              operatorLabel: null,
              value: null,
            },
          ],
        },
      },
      editedItemResetValue: {
        ruleList: {
          assignee: null,
          editConfirmed: false,
          filterList: [
            {
              attributeName: null,
              attributeNameLabel: null,
              dataType: null,
              operator: null,
              operatorLabel: null,
              value: null,
            },
          ],
        },
      },
      selectedDefaultAssignee: null,
      showDefaultAssigneeForm: false,
      showForm: false,
      selectedPackId: null,
      showActivePacksOnly: true,
      isRunningOnDemand: false,
      isLoadingRunHistory: false,
      showNoChangesInfo: false,
      packListError: false,
      runHistoryHeaders: [
        { text: "", value: "data-table-expand", sortable: false, width: "40px" },
        { text: "Date / Time", value: "content.triggeredAt", sortable: false, width: "170px" },
        { text: "Triggered By", value: "displayName", sortable: false, width: "160px" },
        { text: "Mode", value: "content.mode", sortable: false, width: "110px" },
        { text: "Changed", value: "content.totalChanged", sortable: false, width: "90px" },
        { text: "Errors", value: "content.totalErrors", sortable: false, width: "80px" },
      ],
    };
  },
  methods: {
    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedIndex = -1;
      });
    },
    closeEdit() {
      this.dialogEdit = false;
      this.showForm = false;
      this.$nextTick(() => {
        this.editedIndex = -1;
      });
    },
    closeForm() {
      this.editedIndex = -1;
      this.showForm = false;
    },
    deleteRule(item) {
      this.editedIndex = this.formattedAutoConfiguration.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },
    editRule(item) {
      this.showForm = true;
      this.editedIndex = this.formattedAutoConfiguration.indexOf(item);
      const selectedEditItem = _.cloneDeep(this.ruleList[this.editedIndex]);

      this.editedItem = selectedEditItem;
      this.editedItem.editConfirmed = true;
    },
    formatDefaultAssignee(defaultAssignee) {
      if (!defaultAssignee) return;
      return this.formatUsername(defaultAssignee, this.userMap);
    },
    async deleteRuleConfirm() {
      try {
        await this.remove(this.editedIndex);
        this.closeDelete();
        this.$notifySuccess("Rule has been deleted");
        this.editedItem = this.editedItemResetValue;
      } catch (e) {
        this.$notifyError("An error occurred deleting rule");
        this.closeDelete();
      }
    },
    formatFilterList(filterList) {
      return filterList.map(filter => {
        return Object.assign({}, filter, {
          attributeNameLabel: this.getFilterFieldLabel(filter.attributeName),
          operatorLabel: this.getOperatorLabel(filter.operator),
          valueLabel: this.getFilterValueLabel(filter),
        });
      });
    },
    getDynamicDropdownValueLabel(id) {
      if (Array.isArray(id)) {
        const labelList = id.reduce((acc, curr) => {
          const label = this.getDynamicDropdownSingleValueLabel(curr);
          acc.push(label);
          return acc;
        }, []);
        return labelList.join(" —OR— ");
      }
      return this.getDynamicDropdownSingleValueLabel(id);
    },
    getDynamicDropdownSingleValueLabel(id) {
      let dropdownValueLabel;
      let dropdownValueItem;
      const dropdownValue = this.dropdownList.find(item => {
        return item.valueList.find(value => {
          if (value.id === id) {
            dropdownValueItem = value;
            dropdownValueLabel = value.label;
            return true;
          }
        });
      });

      if (!dropdownValue) {
        return UNAVAILABLE_LABEL;
      }

      if (dropdownValue.groupDropdownId) {
        dropdownValueLabel = `${dropdownValueLabel} (${this.getDynamicDropdownSingleValueLabel(
          dropdownValueItem.groupDropdownValueId
        )})`;
      }
      return dropdownValueLabel;
    },
    getHardCodedDropdownList(valueListType) {
      if (!valueListType) {
        return [];
      }

      if (valueListType === "testingStatus") {
        return this.dropdowns.testingStatus.map(status => status.value);
      }

      return this.dropdowns[valueListType];
    },
    getFilterFieldLabel(attributeName) {
      if (!attributeName) return;

      const field = this.allSearchableFields.find(field => field.value === attributeName);
      return field?.name || attributeName;
    },
    getFilterValueLabel(filter = {}) {
      const { attributeName, dataType, operator, value } = cloneDeep(filter);

      if (operator === "csv") {
        const tempValue = value.split(`,`).map(x => +x);
        return tempValue.join(" —OR— ");
      }

      if (dataType === "integerBoolean") {
        return value ? "Yes" : "No";
      }

      if (dataType === "pack" && valueType === "array") {
        const nameList = value.map(this.getPackLabel);
        return nameList.join(" —OR— ");
      }

      if (dataType === "pack") {
        return this.getPackLabel(value);
      }

      if (
        dataType === "dynamicDropdown" ||
        (attributeName === "primaryActivityDropdownValueId" || attributeName === "applicationDropdownValueId")
      ) {
        return this.getDynamicDropdownValueLabel(value);
      }

      let tempValue = value;
      if (Array.isArray(value)) {
        if (attributeName === "assignedTo") {
          tempValue = value.map(val => (val === null ? "Unassigned" : val));
        }
        return tempValue.join(" —OR— ");
      }
      return `${filter.value.toString()}`;
    },
    getOperatorLabel(operator) {
      if (!operator) return;

      if (operator === "in") {
        return "=";
      }

      if (operator === "notIn") {
        return "!=";
      }

      if (operator === "excludes") {
        return "does not contain";
      }

      if (operator === "csv") {
        return "=";
      }

      return operator;
    },
    getPackLabel(packId) {
      const pack = this.getPackById(packId);
      return pack ? pack.title : packId;
    },
    isMovementUpArrowDisplayed(item) {
      return this.formattedAutoConfiguration.indexOf(item) > 0;
    },
    isMovementDownArrowDisplayed(item) {
      return this.formattedAutoConfiguration.indexOf(item) + 1 < this.formattedAutoConfiguration.length;
    },
    loadDefaultAssigneeForm() {
      this.selectedDefaultAssignee = this.defaultAssignee;
      this.showDefaultAssigneeForm = true;
    },
    async moveRule(item, elementModifier) {
      const fromIndex = this.formattedAutoConfiguration.indexOf(item);
      const modifierIndex = elementModifier > 0 ? fromIndex - 1 : fromIndex + 1;
      const toIndex = this.formattedAutoConfiguration.indexOf(this.formattedAutoConfiguration[modifierIndex]);
      if (toIndex < this.ruleList.length) {
        const payload = {
          fromIndex: fromIndex,
          toIndex: toIndex,
        };
        await this.adjustRule(payload);
      }
    },
    async refreshRuleTable() {
      await this.updateTable();
    },
    async saveDefaultAssignee() {
      try {
        await this.assignDefaultAssignee(this.selectedDefaultAssignee);
        this.$notifySuccess("Default assignee has been updated");
        this.showDefaultAssigneeForm = false;
      } catch (e) {
        console.error(e);
        this.$notifyError("An error occurred saving default assignee");
      }
    },
    updateEditIndex(value) {
      this.editedIndex = value;
    },
    formatDateTime(isoString) {
      if (!isoString) return "—";
      const date = new Date(isoString);
      return date.toLocaleString();
    },
    async onPackSelected(packId) {
      if (!packId) return;
      this.$store.commit("autoAssignment/setRunHistory", []);
      this.isLoadingRunHistory = true;
      try {
        await this.fetchRunHistory(packId);
      } catch (e) {
        this.$notifyError("An error occurred fetching run history");
      } finally {
        this.isLoadingRunHistory = false;
      }
    },
    async runOnDemand() {
      this.isRunningOnDemand = true;
      this.showNoChangesInfo = false;
      try {
        const response = await this.$store.dispatch("autoAssignment/runOnDemand", this.selectedPackId);
        const { totalChanged, totalErrors } = response.data.result;
        if (totalChanged > 0 && totalErrors === 0) {
          this.$notifySuccess(`${totalChanged} assignment(s) updated`);
        } else if (totalChanged > 0 && totalErrors > 0) {
          this.$notifyWarn(`${totalChanged} assigned, ${totalErrors} could not be assigned`);
        } else if (totalErrors > 0) {
          this.$notifyError("Auto assignment encountered errors — no release notes were assigned");
        } else {
          this.showNoChangesInfo = true;
        }
        await this.onPackSelected(this.selectedPackId);
      } catch (e) {
        this.$notifyError("An error occurred running auto assignment");
      } finally {
        this.isRunningOnDemand = false;
      }
    },
    ...mapActions({
      fetchDropdownList: "admin/dropdown/list",
      fetchPackList: "pack/getList",
      adjustRule: "autoAssignment/moveRule",
      assignDefaultAssignee: "autoAssignment/assignDefaultAssignee",
      remove: "autoAssignment/remove",
      updateTable: "autoAssignment/get",
      fetchRunHistory: "autoAssignment/fetchRunHistory",
    }),
  },
};
</script>

<style scoped>
.fade-form-enter-active,
.fade-form-leave-active {
  transition: opacity 0.5s;
}

.fade-form-enter,
.fade-form-leave-to {
  opacity: 0;
}

.fade-grid-enter-active,
.fade-grid-leave-active {
  transition: opacity 0.5s;
}

.fade-grid-enter,
.fade-grid-leave-to {
  opacity: 0;
}

::v-deep .auto-assign-instructions .v-expansion-panel::before {
  box-shadow: none;
}

::v-deep .auto-assign-instructions .v-expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

::v-deep .auto-assign-instructions .v-expansion-panel-header__icon .v-icon {
  font-size: 18px;
}

::v-deep .auto-assign-instructions .v-expansion-panel-content__wrap {
  padding: 16px 24px 16px;
}

::v-deep .v-data-table__expanded__content {
  box-shadow: none !important;
  background-color: #f5f5f5;
}

::v-deep [data-cy="auto-assignment-run-history-table"] tbody tr:hover:not(.v-data-table__expanded__content),
::v-deep [data-cy="auto-assignment-run-history-table"] tbody tr:has(+ .v-data-table__expanded__content) {
  background-color: #ffffbf !important;
}

::v-deep [data-cy="auto-assignment-run-history-table"] .v-data-table__expand-icon {
  color: #1976d2;
}

::v-deep [data-cy="auto-assignment-run-history-table"] .v-data-table__expand-icon:focus {
  outline: 2px solid #1976d2 !important;
  outline-offset: 2px !important;
}

.run-history-changes-table {
  table-layout: fixed;
  width: calc(100% - 48px);
  border-collapse: collapse;
  background: transparent;
  font-size: 13px;
  margin-left: 48px;
  border-left: 3px solid #1976d2;
}

.run-history-changes-table th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  padding: 4px 16px 4px 0;
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
}

.run-history-changes-table th:first-child,
.run-history-changes-table td:first-child {
  padding-left: 20px;
}

.run-history-changes-table td {
  padding: 6px 16px 6px 0;
  border-bottom: thin solid rgba(0, 0, 0, 0.08);
}

.run-history-changes-table tr:last-child td {
  border-bottom: none;
}

.run-history-changes-table tr:hover td {
  background-color: #ffffbf;
}
</style>
